import React, { useState, useEffect, useRef } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import { Brain, Twitter, CircleDollarSign, TrendingUp, BarChart3, ExternalLink } from 'lucide-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import type { SubnetMindshareHistory, MindshareHistoryEntry } from '../types/mindshare';
import { downloadChartAsPng } from '../utils/chartHelpers';
import { subnetDescriptions } from '../utils/subnetDescriptions';
import { formatDistanceToNow } from 'date-fns';

interface MindshareHistoryChartProps {
  historyData: SubnetMindshareHistory | null;
  loading: boolean;
  subnetPrice?: {
    timestamps: string[];
    prices: number[];
  };
}

interface TweetData {
  created_at: string;
  engagement_score: number;
  id: string;
  text: string;
  profile_image: string;
  link: string;
  username?: string;
}

export function MindshareHistoryChart({ historyData, loading, subnetPrice }: MindshareHistoryChartProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const chartRef = useRef<any>(null);
  const [highEngagementTweets, setHighEngagementTweets] = useState<TweetData[]>([]);
  const [hoveredTweet, setHoveredTweet] = useState<TweetData | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{x: number, y: number} | null>(null);
  const [expandedTweets, setExpandedTweets] = useState<Record<string, boolean>>({});

  // Fetch high engagement tweets for this subnet
  useEffect(() => {
    const fetchHighEngagementTweets = async () => {
      if (!historyData) return;
      
      try {
        const subnetName = historyData.name;
        // Calculate date 7 days ago for filtering
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        // Special case for TaoHash (SN14)
        const isSubnet14 = historyData.netuid === 14;
        
        if (isSubnet14) {
          // For TaoHash, use both "TaoHash" and "TAOHash" collections to ensure we get tweets
          const taoHashTweets = await fetchTweetsForSubnet("TaoHash", sevenDaysAgo);
          const taohashTweets = await fetchTweetsForSubnet("TAOHash", sevenDaysAgo);
          
          // Combine both sets of tweets
          const allTweets = [...taoHashTweets, ...taohashTweets];
          
          // Remove duplicates (if any)
          const uniqueTweets = allTweets.filter((tweet, index, self) => 
            index === self.findIndex(t => t.id === tweet.id)
          );
          
          setHighEngagementTweets(uniqueTweets);
          return;
        } else {
          // For other subnets, use the normal approach
          const tweetsRef = collection(db, 'subnets', subnetName, 'users');
          const usersSnapshot = await getDocs(tweetsRef);
          
          const allTweets: TweetData[] = [];
          
          // For each user, get their tweets
          for (const userDoc of usersSnapshot.docs) {
            const username = userDoc.id;
            const userTweetsRef = collection(db, 'subnets', subnetName, 'users', username, 'tweets');
            const tweetsQuery = query(userTweetsRef, where('engagement_score', '>', 50));
            const tweetsSnapshot = await getDocs(tweetsQuery);
            
            tweetsSnapshot.forEach(tweetDoc => {
              const tweetData = tweetDoc.data() as TweetData;
              // Only include tweets from the last 7 days
              const tweetDate = new Date(tweetData.created_at);
              if (tweetDate >= sevenDaysAgo) {
                allTweets.push({
                  ...tweetData,
                  id: tweetDoc.id,
                  username
                });
              }
            });
          }
          
          setHighEngagementTweets(allTweets);
        }
      } catch (error) {
        console.error('Error fetching high engagement tweets:', error);
      }
    };
    
    fetchHighEngagementTweets();
  }, [historyData]);

  // Helper function to fetch tweets for a specific subnet name
  const fetchTweetsForSubnet = async (subnetName: string, sevenDaysAgo: Date): Promise<TweetData[]> => {
    try {
      const tweetsRef = collection(db, 'subnets', subnetName, 'users');
      const usersSnapshot = await getDocs(tweetsRef);
      
      const allTweets: TweetData[] = [];
      
      // For each user, get their tweets
      for (const userDoc of usersSnapshot.docs) {
        const username = userDoc.id;
        const userTweetsRef = collection(db, 'subnets', subnetName, 'users', username, 'tweets');
        const tweetsQuery = query(userTweetsRef, where('engagement_score', '>', 50));
        const tweetsSnapshot = await getDocs(tweetsQuery);
        
        tweetsSnapshot.forEach(tweetDoc => {
          const tweetData = tweetDoc.data() as TweetData;
          // Only include tweets from the last 7 days
          const tweetDate = new Date(tweetData.created_at);
          if (tweetDate >= sevenDaysAgo) {
            allTweets.push({
              ...tweetData,
              id: tweetDoc.id,
              username
            });
          }
        });
      }
      
      return allTweets;
    } catch (error) {
      console.error(`Error fetching tweets for subnet ${subnetName}:`, error);
      return [];
    }
  };

  const toggleTweetExpansion = (tweetId: string) => {
    setExpandedTweets(prev => ({
      ...prev,
      [tweetId]: !prev[tweetId]
    }));
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading history data...</div>
      </div>
    );
  }

  if (!historyData || historyData.history.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-400">No history data available</div>
      </div>
    );
  }

  // Sort history by timestamp (oldest to newest)
  const sortedHistory = [...historyData.history].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  // Filter for last 7 days and sample every 15th point (increased from 10 to 15)
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const filteredHistory = sortedHistory.filter(entry => new Date(entry.timestamp) >= sevenDaysAgo);

  // If no data after filtering, show a message
  if (filteredHistory.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-400">No data available</div>
      </div>
    );
  }

  // Get subnet profile image URL if available
  const getSubnetProfileImageUrl = (): string | undefined => {
    if (!historyData) return undefined;
    
    const subnetId = historyData.netuid;
    
    // Special case for subnet 14
    if (subnetId === 14) {
      return "https://pbs.twimg.com/profile_images/1649081080692695051/taohash_400x400.jpg";
    }
    
    const subnetInfo = subnetDescriptions[subnetId];
    
    if (!subnetInfo?.x) return undefined;
    
    // Extract username from X/Twitter URL
    const xUrlMatch = subnetInfo.x.match(/twitter\.com\/(\w+)|x\.com\/(\w+)/i);
    if (!xUrlMatch) return undefined;
    
    const xUsername = (xUrlMatch[1] || xUrlMatch[2] || '').toLowerCase();
    
    return `https://unavatar.io/twitter/${xUsername}`;
  };

  // Prepare chart data for mindshare with price overlay
  const getChartData = () => {
    // Declare these variables at the top of the mindshare block
    let filteredPriceData: number[] = [];
    let filteredTimestamps: number[] = [];
    
    const datasets = [
      {
        label: 'Mindshare Percentage',
        data: filteredHistory.map(entry => entry.mindsharePercentage),
        borderColor: 'rgba(6, 182, 212, 1)', // Cyan
        backgroundColor: 'rgba(6, 182, 212, 0.4)', // Increased opacity from 0.2 to 0.4
        borderWidth: 1, // Increased from 1 to 2
        borderRadius: 3,
        barThickness: 2, // Added to make bars thicker
        order: 1, // Higher order means it's drawn first (behind)
        // Disable hover interactions for mindshare bars
        hoverBackgroundColor: 'rgba(6, 182, 212, 0.4)',
        hoverBorderColor: 'rgba(6, 182, 212, 1)',
        hoverBorderWidth: 1
      }
    ];
    
    // Add price dataset if available
    if (subnetPrice && subnetPrice.timestamps.length > 0) {
      // Create an array of price data points that align with the mindshare timestamps
      const alignedPrices = [];
      const mindshareTimestamps = filteredHistory.map(entry => new Date(entry.timestamp).getTime());
      
      // Get min and max timestamps from mindshare data
      const minTimestamp = Math.min(...mindshareTimestamps);
      const maxTimestamp = Math.max(...mindshareTimestamps);
      
      // Convert price timestamps to Date objects
      const priceTimestamps = subnetPrice.timestamps.map(ts => new Date(ts).getTime());
      
      // Filter price data to match the mindshare time range
      filteredPriceData = subnetPrice.prices.filter((_, index) => {
        const timestamp = priceTimestamps[index];
        return timestamp >= sevenDaysAgo.getTime();
      });
      
      filteredTimestamps = priceTimestamps.filter(ts => ts >= sevenDaysAgo.getTime());
      
      datasets.push({
        label: 'Price',
        data: filteredPriceData.map((price, index) => ({
          x: new Date(filteredTimestamps[index]),
          y: price
        })),
        borderColor: 'rgba(255, 255, 255, 1)', // White line
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 2,
        tension: 0.4,
        type: 'line' as const,
        yAxisID: 'y-price',
        pointRadius: 0,
        pointHoverRadius: 5,
        fill: false,
        order: 0, // Lower order means it's drawn last (in front)
        // Enable hover interactions for price line
        hoverBorderWidth: 3,
        hoverBorderColor: 'rgba(255, 255, 255, 1)'
      });
    }
    
    // Add high engagement tweets as points on the chart
    if (highEngagementTweets.length > 0) {
      // Create a dataset for tweet markers
      const tweetMarkers = highEngagementTweets.map(tweet => {
        const tweetTime = new Date(tweet.created_at).getTime();
        
        // Find the closest price point to this tweet time
        let closestPriceIndex = 0;
        let minTimeDiff = Infinity;
        
        for (let i = 0; i < filteredTimestamps.length; i++) {
          const timeDiff = Math.abs(filteredTimestamps[i] - tweetTime);
          if (timeDiff < minTimeDiff) {
            minTimeDiff = timeDiff;
            closestPriceIndex = i;
          }
        }
        
        // Use the price value at the closest timestamp
        const priceValue = filteredPriceData[closestPriceIndex] || 0;
        
        return {
          x: new Date(tweet.created_at),
          y: priceValue, // Position based on price value
          tweet: tweet
        };
      });
      
      if (tweetMarkers.length > 0) {
        datasets.push({
          data: tweetMarkers,
          backgroundColor: 'rgba(239, 68, 68, 0.8)', // Red circles
          borderColor: 'rgba(255, 255, 255, 1)',
          borderWidth: 1,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointStyle: function(context: any) {
            const index = context.dataIndex;
            const value = context.dataset.data[index];
            
            if (value && value.tweet && value.tweet.profile_image) {
              // Create a canvas for the circular profile image
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              const size = 30; // Size of the final image
              canvas.width = size;
              canvas.height = size;
              
              if (ctx) {
                // Draw circular clipping path
                ctx.beginPath();
                ctx.arc(size/2, size/2, size/2 - 1, 0, Math.PI * 2);
                ctx.closePath();
                ctx.clip();
                
                // Create image element
                const image = new Image();
                image.src = value.tweet.profile_image;
                
                // Draw the image once it's loaded
                image.onload = () => {
                  ctx.drawImage(image, 0, 0, size, size);
                  
                  // Draw border
                  ctx.strokeStyle = 'white';
                  ctx.lineWidth = 2;
                  ctx.beginPath();
                  ctx.arc(size/2, size/2, size/2 - 1, 0, Math.PI * 2);
                  ctx.stroke();
                };
                
                return canvas;
              }
              
              // Fallback to a simple image if canvas fails
              const image = new Image(16, 16);
              image.src = value.tweet.profile_image;
              return image;
            }
            
            return 'circle';
          },
          type: 'scatter' as const,
          yAxisID: 'y-price',
          order: -1, // Highest priority to be drawn on top
          hoverBackgroundColor: 'rgba(239, 68, 68, 1)'
        });
      }
    }
    
    return { 
      labels: filteredHistory.map(entry => new Date(entry.timestamp)),
      datasets
    };
  };

  // Get chart options
  const getChartOptions = () => {
    const isMobile = window.innerWidth < 768;
    
    // Find min and max price values for setting y-axis range
    const prices = subnetPrice?.prices.filter((_, index) => {
      const timestamp = new Date(subnetPrice.timestamps[index]).getTime();
      return timestamp >= sevenDaysAgo.getTime();
    }) || [];
    
    const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
    const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
    
    // Add a small buffer (5%) to min and max for better visualization
    const buffer = (maxPrice - minPrice) * 0.05;
    const yMin = Math.max(0, minPrice - buffer); // Don't go below 0
    const yMax = maxPrice + buffer;
    
    // Base options for chart
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      scales: {
        x: {
          type: 'time' as const,
          time: {
            unit: 'day' as const, // Changed from 'hour' to 'day' for 7-day view
            tooltipFormat: 'PPP',
            displayFormats: {
              day: 'MMM d' // Show month and day
            }
          },
          grid: {
            display: false
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.6)',
            font: {
              size: window.innerWidth < 768 ? 10 : 12
            },
            maxRotation: 0,
            padding: window.innerWidth < 768 ? 5 : 10,
            maxTicksLimit: isMobile ? 4 : 7 // Show up to 7 days
          },
          border: {
            display: false
          }
        },
        y: {
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.6)',
            font: {
              size: window.innerWidth < 768 ? 10 : 12
            }
          },
          border: {
            display: false
          },
          title: {
            display: true,
            text: 'Mindshare %',
            color: 'rgba(255, 255, 255, 0.6)'
          },
          // Disable hover interactions for mindshare y-axis
          display: true
        }
      },
      plugins: {
        legend: {
          display: true,
          labels: {
            color: 'rgba(255, 255, 255, 0.8)',
            font: {
              size: window.innerWidth < 768 ? 10 : 12
            },
            padding: window.innerWidth < 768 ? 10 : 12,
            usePointStyle: true,
            filter: function(item: any) {
              // Only show Mindshare and Price in the legend
              return item.text === 'Mindshare Percentage' || item.text === 'Price';
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'rgba(255, 255, 255, 0.8)',
          bodyColor: 'rgba(255, 255, 255, 0.8)',
          callbacks: {
            title: (context: any) => {
              // Add safety checks for context and parsed property
              if (!context || !Array.isArray(context) || context.length === 0) {
                return '';
              }
              
              const firstContext = context[0];
              if (!firstContext) {
                return '';
              }
              
              // Handle different data structures
              let timestamp;
              if (firstContext.parsed && firstContext.parsed.x !== undefined) {
                timestamp = firstContext.parsed.x;
              } else if (firstContext.raw && typeof firstContext.raw === 'object' && firstContext.raw.x) {
                timestamp = firstContext.raw.x;
              } else if (firstContext.label) {
                timestamp = new Date(firstContext.label).getTime();
              } else {
                return '';
              }
              
              const date = new Date(timestamp);
              return date.toLocaleString('en-US', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZoneName: 'short'
              });
            },
            label: (context: any) => {
              // Only show tooltip for price dataset
              if (context.dataset.label !== 'Price' && context.dataset.type !== 'scatter') {
                return null;
              }
              
              // Check if this is a high engagement tweet point
              if (context.dataset.type === 'scatter') {
                // Don't show label for tweet points - we'll use a custom tooltip
                return null;
              }
              
              const dataIndex = context.dataIndex;
              const entry = filteredHistory[dataIndex];
              const percentChangeText = entry?.percentChange !== undefined ? 
                ` (${entry.percentChange >= 0 ? '+' : ''}${entry.percentChange.toFixed(2)}%)` : '';
              
              if (context.dataset.label === 'Price') {
                // Make sure we're accessing the y property for price data points
                const value = context.raw && typeof context.raw === 'object' ? context.raw.y : context.raw;
                
                // Find the corresponding mindshare value for this timestamp
                let mindshareValue = null;
                if (entry) {
                  mindshareValue = entry.mindsharePercentage;
                }
                
                // Return both price and mindshare values
                if (mindshareValue !== null) {
                  return [
                    `Price: ${value.toFixed(4)} τ`,
                    `Mindshare: ${mindshareValue.toFixed(2)}%`
                  ];
                }
                
                return `Price: ${value.toFixed(4)} τ`;
              }
              
              return `Mindshare: ${context.raw.toFixed(2)}%${percentChangeText}`;
            }
          },
          filter: function(tooltipItem: any) {
            // Only show tooltips for price dataset and scatter points
            return tooltipItem.dataset.label === 'Price' || tooltipItem.dataset.type === 'scatter';
          }
        }
      },
      onHover: (event: any, elements: any[]) => {
        // Handle hover for tweet points
        if (elements.length > 0) {
          const element = elements[0];
          const datasetIndex = element.datasetIndex;
          const index = element.index;
          
          // Check if this is a tweet marker
          if (datasetIndex >= 0 && chartRef.current?.data?.datasets[datasetIndex]?.type === 'scatter') {
            const tweetData = chartRef.current.data.datasets[datasetIndex].data[index];
            if (tweetData && tweetData.tweet) {
              setHoveredTweet(tweetData.tweet);
              
              // Set tooltip position
              if (event && event.native) {
                setTooltipPosition({
                  x: event.native.clientX,
                  y: event.native.clientY
                });
              }
              
              // Change cursor to pointer
              event.native.target.style.cursor = 'pointer';
              return;
            }
          }
        }
        
        // Reset hover state if not hovering over a tweet
        if (hoveredTweet) {
          setHoveredTweet(null);
          setTooltipPosition(null);
        }
        
        // Set cursor based on whether we're hovering over an element
        if (event && event.native) {
          event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
      },
      onClick: (event: any, elements: any[]) => {
        if (elements.length > 0) {
          const element = elements[0];
          const datasetIndex = element.datasetIndex;
          const index = element.index;
          
          // Check if this is a tweet marker
          if (chartRef.current?.data?.datasets[datasetIndex]?.type === 'scatter') {
            const tweetData = chartRef.current.data.datasets[datasetIndex].data[index];
            if (tweetData && tweetData.tweet && tweetData.tweet.link) {
              window.open(tweetData.tweet.link, '_blank');
            }
          }
        }
      }
    };

    // Mindshare-specific options (bar chart with price overlay)
    if (subnetPrice && subnetPrice.timestamps.length > 0) {
      return {
        ...baseOptions,
        scales: {
          ...baseOptions.scales,
          y: {
            ...baseOptions.scales.y,
            title: {
              display: true,
              text: 'Mindshare %',
              color: 'rgba(255, 255, 255, 0.6)'
            }
          },
          'y-price': {
            position: 'right' as const,
            grid: {
              display: false
            },
            ticks: {
              color: 'rgba(255, 255, 255, 0.8)',
              font: {
                size: isMobile ? 10 : 12
              },
              // Exactly 5 ticks between min and max price
              count: 5,
              padding: 10,
              callback: (value: number) => value.toFixed(4)
            },
            border: {
              display: false
            },
            title: {
              display: true,
              text: 'Price (τ)',
              color: 'rgba(255, 255, 255, 0.8)'
            },
            // Set min and max values with buffer
            min: yMin,
            max: yMax
          }
        },
        plugins: {
          ...baseOptions.plugins,
          tooltip: {
            ...baseOptions.plugins.tooltip,
            filter: function(tooltipItem: any) {
              // Only show tooltips for price dataset and scatter points
              return tooltipItem.dataset.label === 'Price' || tooltipItem.dataset.type === 'scatter';
            }
          }
        }
      };
    }

    // Default options
    return baseOptions;
  };

  return (
    <div className="flex flex-col md:flex-row gap-4">
      <div className="w-full md:w-2/3">
        <div className="h-[350px] bg-black/20 backdrop-blur-sm rounded-lg p-2 md:p-4 border border-white/5">
          <Bar ref={chartRef} data={getChartData()} options={getChartOptions()} />
        </div>
        
        {/* Custom Tweet Tooltip - Fixed position for better visibility */}
        {hoveredTweet && tooltipPosition && (
          <div 
            className="fixed z-50 bg-black/90 backdrop-blur-sm rounded-lg p-3 border border-white/20 shadow-xl max-w-xs"
            style={{
              left: tooltipPosition.x + 10,
              top: tooltipPosition.y - 10,
              transform: 'translateY(-100%)'
            }}
          >
            <div className="flex items-start gap-2">
              {hoveredTweet.profile_image && (
                <img 
                  src={hoveredTweet.profile_image} 
                  alt="Profile" 
                  className="w-10 h-10 rounded-full flex-shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png';
                  }}
                />
              )}
              <div>
                <div className="font-bold text-sm">
                  {hoveredTweet.username ? `@${hoveredTweet.username}` : 'User'}
                </div>
                <p className="text-xs text-gray-300 mt-1 line-clamp-3">{hoveredTweet.text}</p>
                <div className="mt-2 text-xs text-cyan-400 flex items-center">
                  <Twitter className="w-3 h-3 mr-1" />
                  <span>Click to view on X</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {highEngagementTweets.length > 0 && (
          <div className="mt-2 text-xs text-gray-400 text-center">
            <span>Red dots represent high-engagement tweets. Hover to see details, click to open.</span>
          </div>
        )}
      </div>
      
      {/* Smart Feed Section */}
      <div className="w-full md:w-1/3">
        <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5 h-[350px] overflow-y-auto">
          <div className="flex items-center space-x-2 mb-4">
            <Twitter className="w-5 h-5 text-cyan-400" />
            <h3 className="text-lg font-semibold">Smart Feed</h3>
          </div>
          
          {highEngagementTweets.length > 0 ? (
            <div className="space-y-4">
              {highEngagementTweets
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .map(tweet => (
                <div key={tweet.id} className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
                  <div className="flex items-center">
                    <img 
                      src={tweet.profile_image || "https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png"}
                      alt="Profile"
                      className="w-10 h-10 rounded-full flex-shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png";
                      }}
                    />
                    <div className="ml-3 flex items-center">
                      <span className="font-bold text-sm">@{tweet.username || 'User'}</span>
                      <span className="text-gray-400 text-xs ml-2">
                        {formatDistanceToNow(new Date(tweet.created_at), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-2">
                    <p className={`text-sm ${expandedTweets[tweet.id] ? '' : 'line-clamp-3'}`}>
                      {tweet.text}
                    </p>
                    
                    {tweet.text.length > 150 && (
                      <button 
                        onClick={() => toggleTweetExpansion(tweet.id)}
                        className="text-cyan-400 hover:text-cyan-300 text-xs mt-1"
                      >
                        {expandedTweets[tweet.id] ? 'Show Less' : 'Show More'}
                      </button>
                    )}
                  </div>
                  
                  <div className="flex justify-end mt-2">
                    <a 
                      href={tweet.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1 text-xs"
                    >
                      <span>View on X</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-[calc(100%-40px)] text-gray-400">
              No high-engagement tweets found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}